import type { FC, SVGProps } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  BillingIcon,
  BridgeIcon,
  BrushIcon,
  House as HouseIcon,
  InformationIcon,
  LogoIcon,
  NavButton,
  SettingsBoldIcon,
  ToolsIcon,
  UsersBoldIcon,
} from '@truvhq/ui';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Link as RouterLink, useHistory, useLocation } from 'react-router-dom';

import { SandboxSwitch } from 'components/SandboxSwitch/SandboxSwitch';
import { useGetBillingProfile } from 'entities/billing/api/useGetBillingProfile';
import { useGetMembersWaitingForApprove } from 'entities/member/api/useGetMembers';
import { useGetProfile } from 'entities/user/api/useGetProfile';
import { useGetKeys } from 'entities/userKey/api/useGetKeys';
import { useIsGovernmentEnterprise } from 'entities/widget-settings/lib/useIsGovernmentEnterprise';
import { DashboardEvent } from 'features/dashboard/lib/constants';
import { useHasVisibleNotifications } from 'features/notifications/lib/useHasVisibleNotifications';
import { TenantCompanies } from 'features/tenancy/ui/TenantCompanies';
import { useGetTenantCompanies } from 'hooks/company';
import { useImpersonation } from 'hooks/impersonation';
import { trackEvent } from 'shared/lib/analytics/trackEvent';
import { useAuth } from 'shared/lib/auth/useAuth';
import { useFiltersContext } from 'shared/lib/filters/useFiltersContext';
import { useMobileNavStore } from 'shared/lib/mobileNav/useMobileNavStore';
import { useSandboxContext } from 'shared/lib/sandbox/useSandboxContext';
import { useScreenSize } from 'shared/lib/screen/useScreenSize';
import {
  SIDEBAR_WIDTH,
  SIDEBAR_WIDTH_VAR,
  useIsSidebarExpanded,
  useIsSidebarPinned,
  useSidebarStore,
} from 'shared/lib/sidebar/useSidebarStore';
import { useTenancyContext } from 'shared/lib/tenancy/useTenancyContext';
import { urls } from 'shared/urls';

import { InfoExternal } from './InfoExternal';
import { MobileNav } from './MobileNav';
import { ACCORDION_TRANSITION_DURATION, NavItems } from './NavItems';
import { NavRail } from './NavRail';
import { SidebarToggle } from './SidebarToggle';
import { StyledBadge } from './StyledBadge';
import type { Item, State } from './types';
import { useLogoutConfirm } from './useLogoutConfirm';
import { useSubmenuState } from './useSubmenuState';
import { isRoutePart } from './utils/isRoutePart';

// extends accordions by default
const initialState = {
  [urls.home.url()]: true,
  [urls.settings.url()]: false,
  [urls.activity.url()]: true,
  [urls.customization.url()]: false,
  [urls.development.url()]: false,
  [urls.billing.url()]: false,
  currentItem: undefined,
};

const iconProps: SVGProps<SVGSVGElement> = {
  width: 16,
  height: 16,
};

export const NavBar: FC = () => {
  const location = useLocation();
  const { logout, permissions, user } = useAuth();
  const { data: profileSettings } = useGetProfile();
  const history = useHistory();
  const { resetFilters } = useFiltersContext();
  const { keys } = useGetKeys();
  const { stopImpersonation } = useImpersonation();
  const { submenuState, setSubmenuState } = useSubmenuState(initialState as State);
  const sandboxWrapperRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { isSandboxOn, isSandboxVisible } = useSandboxContext();
  const { data: tenantCompanies } = useGetTenantCompanies();
  const { tenantCompanyId, setTenantCompanyId } = useTenancyContext();

  const { data: billingProfile } = useGetBillingProfile();
  const hasVisibleNotifications = useHasVisibleNotifications();
  const { isGovernmentEnterprise, isPending: isGovernmentEnterprisePending } = useIsGovernmentEnterprise();

  const { isMobile } = useScreenSize();
  const isPinned = useIsSidebarPinned();
  const isExpanded = useIsSidebarExpanded();
  const setPinned = useSidebarStore((state) => state.setPinned);
  const setHovered = useSidebarStore((state) => state.setHovered);
  const closeMobileNav = useMobileNavStore((state) => state.close);
  // One company is nothing to switch between, so neither the picker nor the rail's initial for it
  // has anything to say.
  const canSwitchCompany = (tenantCompanies?.length ?? 0) > 1;

  useEffect(() => {
    const expandedAccordion = Object.keys(submenuState).find(
      (key) =>
        submenuState[key] && (submenuState.currentItem === undefined || isRoutePart(submenuState.currentItem, key)),
    );

    let timeout: NodeJS.Timeout | null = null;

    if (expandedAccordion) {
      const targetAccordion = document.getElementById(`${expandedAccordion}-header`)?.parentElement;

      const scrollToTarget = () => {
        if (
          targetAccordion &&
          targetAccordion.getBoundingClientRect().bottom >
            Number(sandboxWrapperRef.current?.getBoundingClientRect().top)
        ) {
          targetAccordion.scrollIntoView();
        }
      };

      // wrap into timeout on accordion expand to wait for the accordion content width
      if (expandedAccordion === submenuState.currentItem) {
        timeout = setTimeout(scrollToTarget, ACCORDION_TRANSITION_DURATION);
      } else {
        scrollToTarget();
      }
    } else {
      const collapsedAccordion = Object.keys(submenuState).find(
        (key) => !submenuState[key] && submenuState.currentItem === key,
      );
      const targetAccordion = document.getElementById(`${collapsedAccordion}-header`);

      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollBy(
          0,
          -(
            Number(targetAccordion?.parentElement?.getBoundingClientRect().height) -
            Number(targetAccordion?.getBoundingClientRect().height)
          ),
        );
      }
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [submenuState]);

  const handleLogout = useCallback(async (): Promise<void> => {
    try {
      resetFilters();
      stopImpersonation();
      await logout();
      history.push('/');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  }, [history, logout, resetFilters, stopImpersonation]);

  // Logging out is one click from a menu that is otherwise all navigation, and there is no undo
  const confirmLogout = useLogoutConfirm(handleLogout);

  const handleItemClick = (currentItem?: string) => {
    setSubmenuState({ ...submenuState, currentItem } as State);
  };

  const { data: membersWaitingForApprove } = useGetMembersWaitingForApprove();

  const role = profileSettings?.role.id;
  const isDeveloper = role === 'developer';
  const isOrdersManager = role === 'orders_manager';
  const isStaff = !!profileSettings?.staff_dashboard_url;
  const isIndividualVerifier = user?.isCompanyIndividual;
  const newKeys = isDeveloper && keys?.hasNewProdOrDevKeys ? 1 : 0;

  // Initial opening of submenu based on current route
  useEffect(() => {
    const key = Object.keys(submenuState).find((href) => isRoutePart(location.pathname, href));
    const isFromNavigate = submenuState.currentItem !== undefined;

    if (key) {
      setSubmenuState({
        ...submenuState,
        [key]: true,
        currentItem: isFromNavigate ? location.pathname : key,
      } as State);
    }
  }, []);

  const menuItems: Item[] = useMemo(
    () => [
      {
        title: urls.home.title,
        href: urls.home.url(),
        permission: urls.home.permission,
        hide: isOrdersManager && isSandboxOn,
        icon: <HouseIcon {...iconProps} />,
        submenu: isSandboxOn
          ? undefined
          : [
              {
                title: urls.reporting.title,
                href: urls.reporting.url(),
                permission: urls.reporting.permission,
              },
              {
                title: urls.performance.title,
                href: urls.performance.url(),
                permission: urls.performance.permission,
              },
              {
                title: urls.managers.title,
                href: urls.managers.url(),
                permission: urls.managers.permission,
                hide: isIndividualVerifier,
              },
            ],
      },
      {
        title: urls.emulator.title,
        href: urls.emulator.url(),
        permission: urls.emulator.permission,
        icon: <BridgeIcon {...iconProps} />,
      },
      {
        title: urls.activity.title,
        href: urls.activity.url(),
        icon: <UsersBoldIcon {...iconProps} />,
        submenu: [
          {
            title: urls.allUsers.title,
            href: urls.allUsers.url(),
            permission: urls.allUsers.permission,
          },
          {
            title: urls.orders.title,
            href: urls.orders.url(),
            permission: urls.orders.permission,
          },
          {
            title: urls.tasks.title,
            href: urls.tasks.url(),
            permission: urls.tasks.permission,
          },
          {
            title: urls.coverage.title,
            href: urls.coverage.url(),
            permission: urls.coverage.permission,
          },
        ],
      },
      {
        title: urls.customization.title,
        href: urls.customization.url(),
        icon: <BrushIcon {...iconProps} />,
        permission: urls.customization.permission,
        submenu: [
          {
            title: urls.branding.title,
            href: urls.branding.url(),
            permission: urls.branding.permission,
            customAction() {
              trackEvent(DashboardEvent.BRANDING_CLICK);
            },
          },
          {
            title: permissions.has('can_view_orders') ? 'Order settings' : 'Bridge settings',
            href: urls.customSettings.url(),
            permission: urls.customSettings.permission,
            customAction() {
              trackEvent(DashboardEvent.ORDER_SETTINGS_CLICK);
            },
          },
          {
            title: urls.templates.title,
            href: urls.templates.url(),
            permission: urls.templates.permission,
          },
          {
            title: urls.fieldsCustomization.title,
            href: urls.fieldsCustomization.url(),
            permission: urls.fieldsCustomization.permission,
            hide: isIndividualVerifier,
            customAction() {
              trackEvent(DashboardEvent.DATA_CLICK);
            },
          },
        ],
      },
      {
        title: urls.development.title,
        href: urls.development.url(),
        icon: <ToolsIcon {...iconProps} />,
        permission: urls.development.permission,
        submenu: [
          {
            title: urls.keys.title,
            href: urls.keys.url(),
            permission: urls.keys.permission,
            info: newKeys ? <StyledBadge badgeContent={newKeys} color="secondary" /> : undefined,
          },
          {
            title: urls.platforms.title,
            href: urls.platforms.url(),
            permission: urls.platforms.permission,
          },
          {
            title: urls.logs.title,
            href: urls.logs.url(),
            permission: urls.logs.permission,
          },
          {
            title: urls.webhooks.title,
            href: urls.webhooks.url(),
            permission: urls.webhooks.permission,
          },
          {
            title: urls.docs.title,
            customAction() {
              window.open(urls.docs.parameterUrl?.(isOrdersManager), '_blank');
            },
            permission: urls.docs.permission,
            info: <InfoExternal className="text-white" />,
          },
        ],
      },
      {
        title: urls.billing.title,
        href: urls.billing.url(),
        icon: <BillingIcon {...iconProps} />,
        permission: urls.billing.permission,
        submenu: [
          {
            title: urls.subscriptions.title,
            href: urls.subscriptions.url(),
            permission: urls.subscriptions.permission,
          },
          {
            title: urls.invoices.title,
            href: urls.invoices.url(),
            permission: urls.invoices.permission,
            // Keep it hidden while settings load so it can't flash in for a government enterprise
            hide: isGovernmentEnterprise || isGovernmentEnterprisePending,
          },
          {
            title: urls.creditLedger.title,
            href: urls.creditLedger.url(),
            permission: urls.creditLedger.permission,
            hide: !billingProfile?.has_credits,
          },
          {
            title: urls.billingUsage.title,
            href: urls.billingUsage.url(),
            permission: urls.billingUsage.permission,
          },
          {
            title: urls.monthlyReport.title,
            href: urls.monthlyReport.url(),
            permission: urls.monthlyReport.permission,
            hide: isIndividualVerifier,
          },
        ],
      },
      {
        title: urls.supportTickets.title,
        customAction() {
          trackEvent(DashboardEvent.SUPPORT_CLICK);
          window.open(urls.supportTickets.url(), '_blank');
        },
        icon: <InformationIcon stroke="currentColor" {...iconProps} />,
        info: <InfoExternal className="text-white" />,
        permission: urls.supportTickets.permission,
      },
      {
        title: urls.settings.title,
        href: urls.settings.url(),
        icon: <SettingsBoldIcon {...iconProps} />,
        submenu: [
          {
            title: urls.company.title,
            href: urls.company.url(),
            permission: urls.company.permission,
          },
          {
            title: urls.team.title,
            href: urls.team.parameterUrl?.() ?? '/',
            info: membersWaitingForApprove ? (
              <StyledBadge badgeContent={membersWaitingForApprove} color="secondary" />
            ) : undefined,
            permission: urls.team.permission,
          },
          {
            title: 'Profile',
            href: urls.profile.url(),
          },
          {
            title: 'Notifications',
            href: urls.notifications.url(),
            hide: !hasVisibleNotifications,
          },
          {
            title: 'Log out',
            customAction: confirmLogout,
          },
        ],
      },
    ],
    [
      isOrdersManager,
      isSandboxOn,
      isIndividualVerifier,
      permissions,
      isStaff,
      newKeys,
      membersWaitingForApprove,
      confirmLogout,
      history,
      billingProfile,
      hasVisibleNotifications,
      isGovernmentEnterprise,
      isGovernmentEnterprisePending,
    ],
  );

  if (isMobile) {
    return <MobileNav onClose={closeMobileNav} onLogout={confirmLogout} />;
  }

  const expandedPanel = (
    <PerfectScrollbar
      className="flex max-h-screen flex-grow flex-col bg-grey100"
      containerRef={(ref) => {
        if (ref) {
          // @ts-expect-error -- perfectScrollbar uses legacy ref
          scrollContainerRef.current = ref;
        }
      }}
      style={{ width: SIDEBAR_WIDTH }}
    >
      <div className="flex items-center justify-between py-8 pl-6 pr-4">
        <RouterLink to="/">
          <LogoIcon className="h-5 w-auto text-white" />
        </RouterLink>
        <SidebarToggle isPinned={isPinned} onToggle={() => setPinned(!isPinned)} />
      </div>
      <div className="relative flex flex-grow flex-col bg-grey100 pt-2">
        <NavItems
          items={menuItems}
          pathname={location.pathname}
          permissions={permissions}
          setSubmenuState={setSubmenuState}
          submenuState={submenuState}
          onItemClick={handleItemClick}
        />
      </div>

      {isSandboxVisible && isStaff && (
        <NavButton
          className="mt-4 mr-3 mb-2 h-8 max-w-full !ml-3 !pl-3"
          title="Staff dashboard"
          onClick={() => {
            window.open(profileSettings.staff_dashboard_url, '_blank');
          }}
        />
      )}
      <div className="sticky bottom-0 flex flex-col bg-grey90">
        {canSwitchCompany && tenantCompanies && (
          <div className="flex h-[52px] flex-col justify-center">
            <TenantCompanies
              selectedCompanyId={tenantCompanyId}
              tenantCompanies={tenantCompanies}
              onSelectCompany={setTenantCompanyId}
            />
          </div>
        )}
        {isSandboxVisible && (
          <div ref={sandboxWrapperRef} className="flex h-[54px] flex-col justify-center border-white/10 border-t-2">
            {/* The sidebar's own insets, the same ones the logo row uses */}
            <div className="px-6">
              <SandboxSwitch invert />
            </div>
          </div>
        )}
      </div>
    </PerfectScrollbar>
  );

  return (
    <nav
      aria-label="Main"
      className="h-full shrink-0 overflow-hidden bg-grey100"
      style={{ width: `var(${SIDEBAR_WIDTH_VAR})` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {isExpanded ? (
        expandedPanel
      ) : (
        <NavRail
          isSandboxOn={isSandboxOn}
          isSandboxVisible={isSandboxVisible}
          items={menuItems}
          pathname={location.pathname}
          permissions={permissions}
          tenantInitial={
            canSwitchCompany ? tenantCompanies?.find(({ id }) => id === tenantCompanyId)?.name?.[0] : undefined
          }
        />
      )}
    </nav>
  );
};
